/**
 * versona_gaze.ts — CIVIL INATTENTION for the crowd (2026-09-10).
 *
 * The old rule turned EVERY settled versona within 4.5 m to face the player
 * and hold the stare: in a plaza of twenty that is twenty faces locked on the
 * traveler — Goffman's "civil inattention" describes the opposite. Strangers
 * in public register each other with a brief glance and look away; an
 * extended direct gaze is the clearest breach, and breaches of that norm run
 * an order of magnitude higher toward a "marked" person (the sociology of
 * public encounters: PMC10508291 / Diefenbach 2025, see the goal report).
 *
 * This pure model gives each versona its own glance rhythm (a hash of its
 * phase), so a crowd never turns as one:
 *   · NOTICE   — when the player first comes within GAZE_NOTICE_M, a short
 *                look (the orient beat), then back to their own business.
 *   · GLANCES  — afterwards, in about two thirds of their private 9–15 s
 *                cycles, a 0.9–1.8 s glance (≈ 7 % of the time at 3 m);
 *                familiar faces look longer and more often.
 *   · CLOSE    — inside the personal zone the look holds (you are in their
 *                space; the server's intrusion ladder answers the rest).
 * The renderer also caps how many may look at once (GAZE_MAX_LOOKERS) —
 * attention in a crowd is scarce, never unanimous.
 *
 * ATTENTION PRIORITY (2026-09-15, the versona review): civil inattention is
 * the DEFAULT for the uninvolved, never the rule for everyone. `attentionOf`
 * ranks what a versona has to look at; only the CASUAL rung counts against
 * the looker cap, so two passers-by glancing can never starve the person you
 * are talking to, the merchant serving you, or the one you just shot:
 *   partner  — in conversation with the player / serving them at the counter
 *   threat   — the player's drawn weapon is on them, they are winding up on
 *              the player, or the player struck them within THREAT_HOLD_S
 *   startle  — an impact landed within STARTLE_M in the last STARTLE_S: the
 *              head turns to the IMPACT, not the player (a blast turns a
 *              whole square, a festival crowd included)
 *   casual   — the glance cycles above, under the cap
 */

/** A settled versona notices a player inside this radius (m). */
export const GAZE_NOTICE_M = 6;
/** Inside the personal zone (Hall, 1.22 m + a shoulder) the look holds. */
export const GAZE_CLOSE_M = 1.6;
/** The first-notice look (s). */
export const GAZE_NOTICE_S = 1.2;
/** At most this many versonas look at the player in the same frame. */
export const GAZE_MAX_LOOKERS = 2;
/** Turn-in / turn-away easing (s). */
export const GAZE_EASE_S = 0.28;

function frac(v: number): number { return v - Math.floor(v); }
/** A cheap deterministic hash in [0, 1). */
function hash01(a: number, b: number): number {
  const s = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return frac(s);
}

export interface GlanceOpts {
  /** the versona's private phase (0..2π, its id hash) */
  phase: number;
  /** now, seconds */
  t: number;
  /** distance to the player (m) */
  dist: number;
  /** the player is a familiar face to this versona (relationship tier ≥ familiar) */
  familiar?: boolean;
  /** when the player entered GAZE_NOTICE_M (seconds, or -Infinity) */
  noticedAt?: number;
}

/**
 * How much of a look the versona gives the player right now: 0 = its own
 * business, 1 = full eye contact. Smooth in time (eased edges) so the head
 * turns rather than snaps.
 */
export function glanceWeight(o: GlanceOpts): number {
  if (o.dist >= GAZE_NOTICE_M) return 0;
  if (o.dist < GAZE_CLOSE_M) return 1;
  // The orient beat on first notice.
  const since = o.t - (o.noticedAt ?? -Infinity);
  if (since >= 0 && since < GAZE_NOTICE_S) return ease(since, GAZE_NOTICE_S);
  // Private cycle: 9–15 s long, a glance window of 0.7–1.5 s at a private offset.
  const p = frac(o.phase / (Math.PI * 2));
  const period = 9 + p * 6;
  const cycle = Math.floor((o.t + p * period) / period);
  const chance = o.familiar ? 0.9 : 0.65;
  if (hash01(cycle, o.phase) >= chance) return 0;
  const win = (0.9 + frac(o.phase * 3.1) * 0.9) * (o.familiar ? 1.6 : 1);
  const start = frac(o.phase * 7.3) * Math.max(0.5, period - win - 1);
  const into = frac((o.t + p * period) / period) * period - start;
  if (into < 0 || into > win) return 0;
  return ease(into, win);
}

/** 0→1→0 over `len` seconds with GAZE_EASE_S ramps at both ends. */
function ease(into: number, len: number): number {
  const r = Math.min(GAZE_EASE_S, len / 2);
  if (into < r) return into / r;
  if (into > len - r) return Math.max(0, (len - into) / r);
  return 1;
}

// ── ATTENTION PRIORITY ───────────────────────────────────────────────────────
export type AttentionKind = 'partner' | 'threat' | 'startle' | 'casual' | 'none';
/** A struck versona keeps eyes on the player this long (s), then eases away. */
export const THREAT_HOLD_S = 6;
/** An impact turns nearby heads for this long (s). */
export const STARTLE_S = 1.6;
/** Impacts within this radius (m) startle. */
export const STARTLE_M = 12;
/** A priority look turns the head faster than a casual glance (per-frame blend). */
export const PRIORITY_TURN = 0.45;
export const CASUAL_TURN = 0.25;

export interface AttentionOpts extends GlanceOpts {
  /** in conversation with the player, or serving them at a counter */
  partner?: boolean;
  /** the player's drawn weapon's soft target is this versona */
  aimedAt?: boolean;
  /** this versona is winding up a blow on the player */
  windingOnMe?: boolean;
  /** when the player last struck this versona (s), or -Infinity */
  struckAt?: number;
  /** when the nearest impact within STARTLE_M landed (s), or -Infinity */
  startleAt?: number;
  /** casual lookers already counted this frame */
  lookers: number;
  /** the pose permits a casual glance (settled, not at work / in a chat / at a rite) */
  casualAllowed?: boolean;
}
export interface Attention { kind: AttentionKind; weight: number }

/** Full hold, then a one-second release at the end (a stare fades, never snaps). */
function holdEase(into: number, len: number): number {
  const r = Math.min(1, len / 2);
  return into > len - r ? Math.max(0, (len - into) / r) : 1;
}

/**
 * What this versona is looking at right now, by priority. Pure and
 * deterministic; the caller aims the head at the player for every kind but
 * 'startle', which aims at the impact.
 */
export function attentionOf(o: AttentionOpts): Attention {
  if (o.partner) return { kind: 'partner', weight: 1 };
  if (o.aimedAt || o.windingOnMe) return { kind: 'threat', weight: 1 };
  const sinceStruck = o.t - (o.struckAt ?? -Infinity);
  if (sinceStruck >= 0 && sinceStruck < THREAT_HOLD_S) return { kind: 'threat', weight: holdEase(sinceStruck, THREAT_HOLD_S) };
  const sinceStartle = o.t - (o.startleAt ?? -Infinity);
  if (sinceStartle >= 0 && sinceStartle < STARTLE_S) return { kind: 'startle', weight: ease(sinceStartle, STARTLE_S) };
  if (o.casualAllowed === false) return { kind: 'none', weight: 0 };
  if (o.dist >= GAZE_NOTICE_M || o.lookers >= GAZE_MAX_LOOKERS) return { kind: 'none', weight: 0 };
  const w = glanceWeight(o);
  return w > 0 ? { kind: 'casual', weight: w } : { kind: 'none', weight: 0 };
}

/** Only casual glances are scarce; a partner, a threat or a blast is never rationed. */
export function countsAgainstCap(kind: AttentionKind): boolean { return kind === 'casual'; }

// ── HEAD LAYER (2026-09-15) ──────────────────────────────────────────────────
/** A head turns at most this far off the body (rad, ≈ 70°). */
export const HEAD_TURN_MAX = 1.2;
/** Per-frame ease of the head toward its target. */
export const HEAD_TURN_EASE = 0.3;
/** The head's turn off the body toward a look direction: the shortest arc, clamped to ±max. */
export function headTurn(bodyYaw: number, lookYaw: number, max = HEAD_TURN_MAX): number {
  let d = lookYaw - bodyYaw;
  d = Math.atan2(Math.sin(d), Math.cos(d));
  return Math.max(-max, Math.min(max, d));
}

/** Shortest-arc blend between two yaws (radians). */
export function blendYaw(from: number, to: number, w: number): number {
  if (w <= 0) return from;
  if (w >= 1) return to;
  let d = to - from;
  d = Math.atan2(Math.sin(d), Math.cos(d));
  return from + d * w;
}
